import cx from 'classnames';
import React from 'react';
import PropTypes from 'prop-types';

import Button from '../../Button';
import ReservationSummary from '../../ReservationSummary';
import Rating from '../../Rating';
import shouldHideRating from './shouldHideRating';
import TranslationProvider, { getTranslation } from '../../../providers/TranslationProvider';
import StarIcon from '../../../svg/icons/star.svg';
import { VENUE_INACTIVE_READONLY } from '../../../constants/optimizelyExperiments';
import useExperiment from '../../Experiments/useExperiment';

import './AccountReservationCard.scss';
import PopupMenu from '../../PopupMenu';
import Edit from '../../../svg/icons/ui-edit.svg';
import CloseThick from '../../../svg/icons/close-thick.svg';

export const MODE_HIDE_RATING = 0;
export const MODE_CAN_RATE = 1;
export const MODE_SHOW_RATING = 2;

export const getComputedMode = (context, reservation) => {
  const hideRating = shouldHideRating(reservation);
  const isPastRes = reservation.pastOrFuture === 'past';
  const hasRating = !!reservation.rating;

  if (hasRating && !hideRating && isPastRes) {
    return MODE_SHOW_RATING;
  }
  if (context !== 'widget' && !hideRating && isPastRes && !hasRating) {
    return MODE_CAN_RATE;
  }
  return MODE_HIDE_RATING;
};

function renderRateBtn(canRate, onRating, reservation) {
  if (!canRate) {
    return null;
  }

  return (
    <Button
      type="button"
      className="AccountReservationCard__content--right AccountReservationCard__no-rating"
      onClick={(evt) => {
        evt.stopPropagation();
        onRating(reservation);
      }}
    >
      <i className="ResyIcon" aria-hidden="true">
        <StarIcon />
      </i>
      <TranslationProvider translationKey="rate">
        <span>Rate</span>
      </TranslationProvider>
    </Button>
  );
}

function renderRating(showRating, rating = 0) {
  if (!showRating) {
    return null;
  }
  return (
    <div className="AccountReservationCard__content--right AccountReservationCard__existing-rating">
      <TranslationProvider translationKey="your_rating">
        <span>Your Rating</span>
      </TranslationProvider>
      <Rating
        scale={5}
        score={rating}
      />
    </div>
  );
}

function renderCtas({
  attendee,
  context,
  isUpcoming,
  getOnClick,
  presentCancelation,
  bookOnBehalfOf,
}) {
  if (!isUpcoming) {
    return null;
  }

  return (
    <PopupMenu
      ariaLabel={getTranslation({
        translationKey: 'reservation_modify_menu',
        fallback: 'Reservation Modify Menu',
      })}
      buttonsArr={
      [
        {
          onClick: getOnClick,
          icon: Edit,
          text: 'Manage',
          translationKey: 'reservation_manage',
          'data-test-id': 'account_reservation_card-button-manage',
        },
        ...(context !== 'widget' && (attendee?.type !== 'guest' || bookOnBehalfOf)) ? [{
          onClick: presentCancelation,
          icon: CloseThick,
          text: 'Cancel',
          translationKey: 'button_cancel',
          iconProps: { fill: '#eb1700' },
          style: { color: '#eb1700' },
          'data-test-id': 'account_reservation_card-button-cancel',
        }] : [],
      ]
      }
      height="24px"
      width="24px"
      testId="account_reservation_card-menu"
    />
  );
}

const AccountReservationCard = ({
  context,
  reservation,
  matchMedia,
  onClick,
  onRating,
  mode,
  past,
  presentCancelation,
  isGda,
  bookOnBehalfOf,
  guestName,
}) => {
  const [isVenueInactiveReadonlyEnabled] = useExperiment(VENUE_INACTIVE_READONLY);
  const backgroundImage = reservation.template?.images?.[0] || reservation.venue?.images?.[0];
  const isUpcoming = reservation.pastOrFuture !== 'past';
  const isInactiveVenue = reservation.venue.is_active === false;
  const isReadOnly = isVenueInactiveReadonlyEnabled
    && isInactiveVenue
    && !past;
  const canRate = mode === MODE_CAN_RATE
    && !(isVenueInactiveReadonlyEnabled && isInactiveVenue);
  const showRating = mode === MODE_SHOW_RATING;

  function getOnClick(clickHandler, resy) {
    if (!clickHandler) {
      return undefined;
    }
    return () => clickHandler(resy);
  }

  const cardContent = (
    <>
      <ReservationSummary
        bookOnBehalfOf={bookOnBehalfOf}
        eventName={reservation.event ? reservation.event.name : ''}
        guestName={guestName}
        imageUrl={backgroundImage}
        isGda={isGda}
        largeImage={matchMedia?.sm}
        largeText={false}
        onClick={isReadOnly ? undefined : getOnClick(onClick, reservation)}
        seats={reservation.seats}
        showCategory
        smallBreakpoint={matchMedia?.sm}
        tableType={reservation.tableType}
        timeslot={{
          day: reservation.day,
          time: reservation.time,
        }}
        venue={reservation.venue.name}
        withRating={canRate || showRating}
      />

      {renderCtas(
        {
          attendee: reservation?.attendee,
          context,
          isUpcoming: !past && !isReadOnly,
          getOnClick: getOnClick(onClick, reservation),
          presentCancelation: () => presentCancelation(reservation),
          bookOnBehalfOf,
        }
      )}
      {renderRateBtn(
        /* context !== 'widget' && !shouldHideRating && pastOrFuture === 'past' && !reservation.rating */
        canRate,
        onRating,
        reservation
      )}
      {renderRating(
        /* !shouldHideRating && pastOrFuture === 'past' && !!reservation.rating */
        showRating,
        reservation.rating
      )}
    </>
  );

  return (
    <div className={cx('AccountReservationCard', {
      AccountReservationCard__upcoming: isUpcoming,
      AccountReservationCard__sm: matchMedia?.sm,
      'AccountReservationCard--inactive-readonly': isReadOnly,
    })}
    >
      {isReadOnly ? (
        <>
          <div className="AccountReservationCard__body">
            {cardContent}
          </div>
          <TranslationProvider translationKey="reservation_inactive_assistance">
            <p className="AccountReservationCard__inactive-message">
              For assistance with this booking, please contact the venue directly.
            </p>
          </TranslationProvider>
        </>
      ) : cardContent}
    </div>
  );
};

AccountReservationCard.propTypes = {
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool.isRequired,
    sm: PropTypes.bool.isRequired,
    md: PropTypes.bool.isRequired,
    ipadLandscape: PropTypes.bool.isRequired,
    lg: PropTypes.bool.isRequired,
    xl: PropTypes.bool.isRequired,
  }).isRequired,
  bookOnBehalfOf: PropTypes.bool,
  context: PropTypes.string,
  guestName: PropTypes.string,
  isGda: PropTypes.bool,
  mode: PropTypes.number,
  onRating: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  past: PropTypes.bool,
  presentCancelation: PropTypes.func.isRequired,
  reservation: PropTypes.shape({
    attendee: PropTypes.shape({
      type: PropTypes.string,
    }),
    pastOrFuture: PropTypes.string,
    event: PropTypes.shape({
      name: PropTypes.string,
    }),
    rating: PropTypes.number,
    isExpired: PropTypes.bool,
    seats: PropTypes.number,
    tableType: PropTypes.string,
    day: PropTypes.string,
    template: PropTypes.shape({
      images: PropTypes.arrayOf(PropTypes.string),
    }),
    time: PropTypes.string,
    venue: PropTypes.shape({
      is_active: PropTypes.bool,
      name: PropTypes.string,
      images: PropTypes.arrayOf(PropTypes.string),
    }),
  }).isRequired,
};

AccountReservationCard.defaultProps = {
  bookOnBehalfOf: false,
  guestName: undefined,
  isGda: false,
  mode: MODE_HIDE_RATING,
  past: false,
  context: '',
};

AccountReservationCard.displayName = 'AccountReservationCard';
export default AccountReservationCard;
